import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { CustomPrice, PaymentMethods, SiteSettings } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SaSlides } from '@/components/generals/sa-slides'
import { Cms } from '@/components/generals/cms'
import { CustomOrderForm } from '@/components/shop/custom-order-form'
import { useT } from '@/lib/i18n'
import { paystackReady } from '@/lib/shop/paystack'

import {
    eftReady,
    getBankDetails,
    getCustomPrices,
    getSettings,
    safely
} from '@/lib/shop/data'

interface CustomOrderPageProps {
    settings: SiteSettings | null
    prices: Array<CustomPrice>
    methods: PaymentMethods
}

const CustomOrderPage: FC<CustomOrderPageProps> = ({ settings, prices, methods }): ReactNode => {
    const t = useT()

    return (
        <Layout settings={settings}>
            <Seo title={t('customTitle')} description={settings?.customText.v} />

            <section id='custom-order-intro' className='sa-photo border-b-2 border-line bg-pink'>
                <SaSlides start='table-mountain' />
                <div className='mx-auto max-w-7xl px-4 py-14 sm:px-6'>
                    {settings
                        ? (
                            <>
                                <Cms as='h1' table='site_settings' id={settings.id} field={settings.customHeading} className='text-5xl sm:text-6xl' />
                                <Cms as='p' table='site_settings' id={settings.id} field={settings.customText} className='mt-4 max-w-2xl text-lg' />
                            </>
                        )
                        : <h1 className='text-5xl sm:text-6xl'>{t('customTitle')}</h1>}
                </div>
            </section>

            <section id='custom-order' className='mx-auto max-w-7xl px-4 py-14 sm:px-6'>
                <CustomOrderForm prices={prices} settings={settings} methods={methods} />
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<CustomOrderPageProps> = async ({ locale }) => {
    const [settings, prices, bank] = await Promise.all([
        safely(() => getSettings(locale), null),
        safely(() => getCustomPrices(), []),
        safely(() => getBankDetails(), null)
    ])

    // Only whether each option is switched on reaches the page, never the bank details themselves.
    const methods: PaymentMethods = { card: paystackReady(), eft: eftReady(bank) }

    return { props: { settings, prices, methods }, revalidate: 30 }
}

export default CustomOrderPage
